import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type ICRTNProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  icrUnits: StringUnion;
  ropRider: boolean;
  cwa: StringUnion;
  groupTrust: boolean;
  section125: boolean;
  replacement: boolean;
  originalAge: number;
  reRate: boolean;
  policyNumber: PolicyNumber;
  productList: PlanModel;
  overRideBillForm: boolean;
};

export class InvalidNewYorkICRTN {
  public readonly key = 'InvalidNewYorkICRTN';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly icrUnits: StringUnion,
    public readonly ropRider: boolean,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: PlanModel,
    public readonly overRideBillForm: boolean
  ) {}
}

export class ValidNewYorkICRTN {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    icrUnits,
    ropRider,
    cwa,
    groupTrust,
    section125,
    replacement,
    originalAge,
    reRate,
    policyNumber,
    productList,
    overRideBillForm,
  }: ICRTNProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      icrUnits,
      ropRider,
      cwa,
      groupTrust,
      section125,
      replacement,
      originalAge,
      reRate,
      policyNumber,
      productList,
      overRideBillForm
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    icrUnits: StringUnion,
    ropRider: boolean,
    cwa: StringUnion,
    groupTrust: boolean,
    section125: boolean,
    replacement: boolean,
    originalAge: number,
    reRate: boolean,
    policyNumber: PolicyNumber,
    productList: PlanModel,
    overRideBillForm: boolean
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      groupTrust &&
      section125 &&
      replacement &&
      originalAge &&
      reRate &&
      policyNumber &&
      isLoadedProducts(productList)
      ? new ValidNewYorkICRTN(
          productType,
          planType,
          premium,
          calculatedPremium,
          icrUnits,
          ropRider,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          productList,
          overRideBillForm
        )
      : new InvalidNewYorkICRTN(
          productType,
          planType,
          premium,
          calculatedPremium,
          icrUnits,
          ropRider,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          productList,
          overRideBillForm
        );
  }

  public readonly key = 'ValidNewYorkICRTN';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly icrUnits: StringUnion,
    public readonly ropRider: boolean,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: Loaded<Plan[]>,
    public readonly overRideBillForm: boolean
  ) {}
}

export type NewYorkICRTN = InvalidNewYorkICRTN | ValidNewYorkICRTN;

export type NewYorkICRTNOptions<TReturn> = ToPartialMatchOptions<NewYorkICRTN, 'key', TReturn>;

export const matchGroupICRTN = <TReturn>(options: NewYorkICRTNOptions<TReturn>) => (app: NewYorkICRTN): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkICRTN':
      return options.InvalidNewYorkICRTN ? options.InvalidNewYorkICRTN(app) : options.default(app);
    case 'ValidNewYorkICRTN':
      return options.ValidNewYorkICRTN ? options.ValidNewYorkICRTN(app) : options.default(app);
  }
};

export const EmptyNewYorkICRTN = new InvalidNewYorkICRTN(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  false,
  false,
  0,
  false,
  EmptyPolicyNumber,
  new UnassignedPlanModel(),
  false
);

export const changeProductType = (productType: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, productType: NonEmptyString.create(productType)});

export const changePlanType = (planType: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, planType: NonEmptyString.create(planType)});

export const changeICRUnits = (icrUnits: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, icrUnits: NonEmptyString.create(icrUnits)});

export const changeROPRider = (ropRider: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, ropRider});

export const changePremium = (premium: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeGroupTrust = (groupTrust: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, groupTrust});

export const changeSection125 = (section125: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, replacement, originalAge: 0});

export const changeOriginalAge = (originalAge: number) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, originalAge});

export const changeReRate = (reRate: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, reRate, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeProductList = (products: PlanModel) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, productList: products});

export const changeOverrideBillForm = (overRideBillForm: boolean) => (app: NewYorkICRTN) =>
  ValidNewYorkICRTN.createApp({...app, overRideBillForm});
